/**
 * Fire-and-forget auth diagnostic logger.
 *
 * Must NOT go through the axios client: that interceptor is the very thing we are diagnosing,
 * and requires a Bearer token we may not have. Uses fetch + keepalive so events survive page
 * unload (redirect to Azure AD, retry-to-login flows).
 */

export interface AuthEventPayload {
  event: string
  errorType?: string
  errorCode?: string
  errorMessage?: string
  forceRefresh?: boolean
  url?: string
  status?: number
  hasAccount?: boolean
  accountHomeId?: string
}

const API_URL = import.meta.env.VITE_API_URL || ''
const SESSION_KEY = 'srr.telemetry.sessionId'

function getSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = crypto.randomUUID()
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return ''
  }
}

export function logAuthEvent(payload: AuthEventPayload): void {
  const body = JSON.stringify({
    ...payload,
    sessionId: getSessionId(),
    clientTimestamp: new Date().toISOString(),
  })

  try {
    void fetch(`${API_URL}/api/telemetry/auth-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // swallowed — telemetry must never break the app
    })
  } catch {
    // swallowed
  }
}
